import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "../axios";
import DepotSelect, { useDepot } from "../components/DepotSelect";
import "../styles/analysis.css";

const STATUS_TABS = [
  { key: "open", label: "未処理" },
  { key: "resolved", label: "解消" },
  { key: "dismissed", label: "問題なし" },
  { key: "", label: "すべて" },
];

/**
 * 要確認（運用解析）。
 *
 * duty:audit が見つけた「観測どうしの矛盾」を並べて、人が裁く画面。
 *
 * ## 直すボタンは置かない
 *
 * 目撃の修正は投稿の編集でやること。ここで直せるようにすると、
 * **矛盾の片方を消すだけで筋の通った記録になったように見えてしまう。**
 * どちらが正しいかは、乗番からの見立てを添えても機械には決められない。
 *
 * 直したかどうかは次の duty:audit が条件の有無で判定して自動で閉じる。
 * ここのボタンは「見たけれど問題なかった」を残すためのもの。
 */
export default function AnalysisAuditPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 営業所は乗番がある営業所から（決め打ちしない。土井を足したときに辿れなくなる）
  const [depot] = useDepot(searchParams, setSearchParams);
  const status = searchParams.get("status") ?? "open";
  const kind = searchParams.get("kind") || "";
  const days = searchParams.get("days") || "30";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    if (!depot) return;      // 既定の営業所を引いている最中
    setLoading(true);
    setError(null);

    const query = new URLSearchParams({ depot, days, ...(status ? { status } : {}), ...(kind ? { kind } : {}) });

    axios
      .get(`/analysis/audit?${query}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.message ?? "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
  }, [depot, status, kind, days]);

  useEffect(load, [load]);

  function setParam(patch) {
    setSearchParams({ depot, status, kind, days, ...patch });
  }

  function judge(flag, next) {
    setSaving(flag.id);
    axios
      .put(`/analysis/audit/${flag.id}`, { status: next })
      .then(load)
      .catch((e) => setError(e.response?.data?.message ?? "更新に失敗しました。"))
      .finally(() => setSaving(null));
  }

  return (
    <div className="anl-page">
      <div className="anl-header">
        <button className="anl-back" onClick={() => navigate("/analysis")}>
          運用解析へ戻る
        </button>
        <h2 className="anl-title">
          要確認
          <DepotSelect depot={depot} onChange={(d) => setParam({ depot: d })} />
        </h2>
        <p className="anl-subtitle">
          {depot}営業所の目撃情報から、つじつまの合わないものを拾っています。
        </p>
      </div>

      <div className="anl-warning">
        <strong>ここに出るものが、そのまま誤りとは限りません。</strong><br />
        たとえば「手放して取り戻す交代」は、車号の誤りのこともあれば、
        乗番の組み立てが誤っていることもあります。
        <strong>自動では直しません。</strong>目撃の修正は投稿の編集から行ってください。
      </div>

      {data && (
        <>
          <div className="anl-tabs">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key || "all"}
                className={`anl-tab ${status === tab.key ? "anl-tab--active" : ""}`}
                onClick={() => setParam({ status: tab.key })}
              >
                {tab.label}
                {tab.key && ` ${data.counts[tab.key] ?? 0}`}
              </button>
            ))}
          </div>

          <div className="anl-tabs">
            <button
              className={`anl-tab ${kind === "" ? "anl-tab--active" : ""}`}
              onClick={() => setParam({ kind: "" })}
            >
              すべての種類
            </button>
            {Object.entries(data.kinds).map(([key, label]) => (
              <button
                key={key}
                className={`anl-tab ${kind === key ? "anl-tab--active" : ""}`}
                onClick={() => setParam({ kind: key })}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      {loading && <p className="anl-empty">読み込み中…</p>}
      {error && <div className="anl-warning">{error}</div>}

      {data && !loading && data.flags.length === 0 && (
        <div className="anl-notice anl-notice--quiet">
          直近{data.days}日に、この条件のものはありません。
        </div>
      )}

      {data &&
        !loading &&
        data.flags.map((flag) => (
          <div key={flag.id} className={`anl-card anl-flag anl-flag--${flag.status}`}>
            <div className="anl-card__header">
              <span>
                <span className="anl-badge anl-badge--low">{flag.kind_label}</span>{" "}
                {flag.service_date}
              </span>
              <span style={{ fontWeight: 400, color: "#6b7280" }}>
                {flag.status === "open"
                  ? "未処理"
                  : flag.status === "resolved"
                    ? `解消${flag.resolved_by ? `（${flag.resolved_by}）` : "（自動）"}`
                    : `問題なし${flag.resolved_by ? `（${flag.resolved_by}）` : ""}`}
              </span>
            </div>

            <div className="anl-card__body">
              <p className="anl-flag__summary">{flag.summary}</p>

              <FlagDetail flag={flag} depot={data.depot} navigate={navigate} />

              <div className="anl-flag__actions">
                {flag.status !== "open" ? (
                  <button
                    className="anl-btn anl-btn--quiet"
                    disabled={saving === flag.id}
                    onClick={() => judge(flag, "open")}
                  >
                    未処理に戻す
                  </button>
                ) : (
                  <>
                    <button
                      className="anl-btn"
                      disabled={saving === flag.id}
                      onClick={() => judge(flag, "resolved")}
                    >
                      直したので解消
                    </button>
                    <button
                      className="anl-btn anl-btn--quiet"
                      disabled={saving === flag.id}
                      onClick={() => judge(flag, "dismissed")}
                    >
                      問題なし
                    </button>
                  </>
                )}
              </div>

              {flag.resolution && <p className="anl-note">{flag.resolution}</p>}
              <p className="anl-note">
                最初に見つけた{flag.detected_at}
                {flag.last_detected_at !== flag.detected_at && ` · 最後に見つけた${flag.last_detected_at}`}
              </p>
            </div>
          </div>
        ))}
    </div>
  );
}

/**
 * 種類ごとの中身。**言い切らないこと。**
 * どれも「こう見える」までしか言えず、正解は人が決める。
 */
function FlagDetail({ flag, depot, navigate }) {
  const d = flag.detail ?? {};

  const toBus = (busNumber) =>
    navigate(
      `/analysis/buses/${encodeURIComponent(busNumber)}?date=${flag.service_date}&depot=${encodeURIComponent(depot)}`
    );

  if (flag.kind === "same_minute_conflict") {
    return (
      <>
        <ul className="anl-flag__list">
          {(d.entries ?? []).map((e) => (
            <li key={e.sighting_id}>
              {e.route} {e.start}発{d.at}
              {e.route_no && ` · ${e.route_no}`}
            </li>
          ))}
        </ul>
        <p className="anl-note">
          同じ車が同じ分に2か所から発車しています。片方が誤りです。
          <button className="anl-link" onClick={() => toBus(d.bus_number)}>
            {d.bus_number}の当日の運用を見る
          </button>
        </p>
      </>
    );
  }

  if (flag.kind === "contested_trip") {
    const v = d.verdict ?? {};

    return (
      <>
        <ul className="anl-flag__list">
          {(d.buses ?? []).map((busNumber) => (
            <li key={busNumber}>
              <button className="anl-link" onClick={() => toBus(busNumber)}>
                {busNumber}
              </button>
              {v.candidates && v.candidates[busNumber] !== undefined && (
                <> — 同じ乗番の他の便を{v.candidates[busNumber]}便</>
              )}
            </li>
          ))}
        </ul>
        <p className="anl-note">
          {v.decided ? (
            <>
              <strong>乗番からの見立ては{v.bus_number}。</strong>
              {v.reason}。ただし目撃そのものを確かめたわけではありません。
            </>
          ) : (
            <>
              <strong>乗番からは決められませんでした。</strong>
              {v.reason}。
            </>
          )}
        </p>
      </>
    );
  }

  if (flag.kind === "short_turnaround") {
    return (
      <p className="anl-note">
        {d.route} {d.start}発を{d.from}と{d.to}に続けて出ています（{d.gap_minutes}分間隔）。
        この始発地の折返しは普段いちばん短いもので{d.cycle_minutes}分です。
        <button className="anl-link" onClick={() => toBus(d.bus_number)}>
          {d.bus_number}の当日の運用を見る
        </button>
      </p>
    );
  }

  if (flag.kind === "bus_swap_return") {
    return (
      <>
        <ul className="anl-flag__list">
          {(d.segments ?? []).map((s, i) => (
            <li key={i}>
              {s.from}〜{s.to}{" "}
              <button className="anl-link" onClick={() => toBus(s.bus_number)}>
                {s.bus_number}
              </button>{" "}
              ({s.trips}便)
            </li>
          ))}
        </ul>
        <p className="anl-note">
          <strong>原因は2通りあります。</strong>
          途中の車号が誤っている場合と、本来2本の乗番が1本に繋がっている場合です。
          {flag.duty_id && (
            <button className="anl-link" onClick={() => navigate(`/analysis/duties/${flag.duty_id}`)}>
              乗番{d.duty_code}を見る
            </button>
          )}
        </p>
      </>
    );
  }

  return null;
}
